import React, { useEffect, useState, Suspense, lazy, ComponentType, useRef } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation, useNavigationType } from 'react-router-dom';
import { useSession, useSupabaseClient } from '@supabase/auth-helpers-react';
import { SEOHead } from './components/SEOHead';
import { Footer } from './components/Footer';
import { Header } from './components/Header';
import { LoadingIndicator } from './components/LoadingIndicator';

import { AnnouncementBar } from './components/AnnouncementBar';
import { CookieConsentBanner } from './components/CookieConsentBanner';
import { useShellData } from './hooks/useHomepageData';
import { ErrorBoundary } from './components/ErrorBoundary';
import { clearAllToolDrafts } from './utils/toolSessionStore';

const USER_COUNTRY_KEY = 'user_country';

function CountryParamCapture() {
  const location = useLocation();
  const hasCheckedInitial = useRef(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const countryParam = urlParams.get('country');

    if (countryParam) {
      const normalizedCountry = countryParam.toUpperCase();
      sessionStorage.setItem(USER_COUNTRY_KEY, normalizedCountry);
    }
  }, [location.search]);

  useEffect(() => {
    if (hasCheckedInitial.current) return;
    hasCheckedInitial.current = true;

    const urlParams = new URLSearchParams(window.location.search);
    const countryParam = urlParams.get('country');

    if (countryParam) {
      const normalizedCountry = countryParam.toUpperCase();
      sessionStorage.setItem(USER_COUNTRY_KEY, normalizedCountry);
    }
  }, []);

  return null;
}

function AnalyticsPageTracker() {
  const location = useLocation();
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    window.gtag?.('event', 'page_view', {
      page_path: location.pathname + location.search,
      page_title: document.title,
    });
  }, [location.pathname, location.search]);

  return null;
}

const lazyRetry = function<T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T } | T>
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    const retryKey = `chunk-retry:${window.location.pathname}`;
    const hasRetried = window.sessionStorage.getItem(retryKey) === '1';

    try {
      const component = await componentImport();
      window.sessionStorage.removeItem(retryKey);
      return component;
    } catch (error) {
      if (!hasRetried) {
        window.sessionStorage.setItem(retryKey, '1');
        const url = new URL(window.location.href);
        url.searchParams.set('_cb', Date.now().toString());
        window.location.replace(url.toString());
        return new Promise(() => {});
      }
      throw error;
    }
  });
};

// Lazy load all route components for better code splitting with retry logic
const LandingPage = lazyRetry(() => import('./components/LandingPage').then(m => ({ default: m.LandingPage })));
const BulkBackgroundRemover = lazyRetry(() => import('./components/BulkBackgroundRemover').then(m => ({ default: m.BulkBackgroundRemover })));
const BlogList = lazyRetry(() => import('./components/blog/BlogList').then(m => ({ default: m.BlogList })));
const BlogPost = lazyRetry(() => import('./components/blog/BlogPost').then(m => ({ default: m.BlogPost })));
const BlogPostPlainText = lazyRetry(() => import('./components/blog/BlogPostPlainText').then(m => ({ default: m.BlogPostPlainText })));
const AdminDashboard = lazyRetry(() => import('./components/admin/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const AdminLogin = lazyRetry(() => import('./components/admin/AdminLogin').then(m => ({ default: m.AdminLogin })));
const ProtectedRoute = lazyRetry(() => import('./components/admin/ProtectedRoute').then(m => ({ default: m.ProtectedRoute })));
const BlogPostsList = lazyRetry(() => import('./components/admin/BlogPostsList').then(m => ({ default: m.BlogPostsList })));
const BlogPostEditor = lazyRetry(() => import('./components/admin/BlogPostEditor').then(m => ({ default: m.BlogPostEditor })));
const ContactPage = lazyRetry(() => import('./components/ContactPage').then(m => ({ default: m.ContactPage })));
const BlogWriter = lazyRetry(() => import('./components/BlogWriter').then(m => ({ default: m.BlogWriter })));
const ProductDescriptionGenerator = lazyRetry(() => import('./components/ProductDescriptionGenerator').then(m => ({ default: m.ProductDescriptionGenerator })));
const ProductCollageMaker = lazyRetry(() => import('./components/ProductCollageMaker').then(m => ({ default: m.ProductCollageMaker })));
const SocialMediaContentGenerator = lazyRetry(() => import('./components/SocialMediaContentGenerator').then(m => ({ default: m.SocialMediaContentGenerator })));
const EmailCopyGenerator = lazyRetry(() => import('./components/EmailCopyGenerator').then(m => ({ default: m.EmailCopyGenerator })));
const PricingPage = lazyRetry(() => import('./components/PricingPage').then(m => ({ default: m.PricingPage })));
const StaticPage = lazyRetry(() => import('./components/StaticPage').then(m => ({ default: m.StaticPage })));
const QAApp = lazyRetry(() => import('./components/QAApp').then(m => ({ default: m.QAApp })));
const UserDashboard = lazyRetry(() => import('./components/UserDashboard').then(m => ({ default: m.UserDashboard })));
const SignUpPage = lazyRetry(() => import('./components/SignUpPage').then(m => ({ default: m.SignUpPage })));
const SignInPage = lazyRetry(() => import('./components/SignInPage').then(m => ({ default: m.SignInPage })));
const ResetPasswordPage = lazyRetry(() => import('./components/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const SetPasswordPage = lazyRetry(() => import('./components/SetPasswordPage').then(m => ({ default: m.SetPasswordPage })));
const BannerCreator = lazyRetry(() => import('./components/BannerCreator').then(m => ({ default: m.BannerCreator })));
const PhotoRetoucher = lazyRetry(() => import('./components/PhotoRetoucher').then(m => ({ default: m.PhotoRetoucher })));
const GiftGuideCreator = lazyRetry(() => import('./components/GiftGuideCreator').then(m => ({ default: m.GiftGuideCreator })));
const ImageResizer = lazyRetry(() => import('./components/ImageResizer').then(m => ({ default: m.ImageResizer })));
const SEOContentAudit = lazyRetry(() => import('./components/SEOContentAudit').then(m => ({ default: m.SEOContentAudit })));
const StoreHealthCheck = lazyRetry(() => import('./components/StoreHealthCheck').then(m => ({ default: m.StoreHealthCheck })));
const AdCopyGenerator = lazyRetry(() => import('./components/AdCopyGenerator').then(m => ({ default: m.AdCopyGenerator })));
const ImageGenerator = lazyRetry(() => import('./components/ImageGenerator').then(m => ({ default: m.ImageGenerator })));
const BannerGenerator = lazyRetry(() => import('./components/BannerGenerator'));
const RedditScraper = lazyRetry(() => import('./components/RedditScraper').then(m => ({ default: m.RedditScraper })));
const PhotoStudio = lazyRetry(() => import('./components/PhotoStudio').then(m => ({ default: m.PhotoStudio })));
const VerifyEmailPage = lazyRetry(() => import('./components/VerifyEmailPage').then(m => ({ default: m.default })));
const NotFoundPage = lazyRetry(() => import('./components/NotFoundPage').then(m => ({ default: m.NotFoundPage })));
const BulkProductDescriptionWriter = lazyRetry(() => import('./components/landing/BulkProductDescriptionWriter').then(m => ({ default: m.BulkProductDescriptionWriter })));
const AutomateDropshippingPhotos = lazyRetry(() => import('./components/landing/AutomateDropshippingPhotos').then(m => ({ default: m.AutomateDropshippingPhotos })));
const AIJewelryPhotos = lazyRetry(() => import('./components/landing/AIJewelryPhotos').then(m => ({ default: m.AIJewelryPhotos })));
const LifestyleImageGeneratorEcommerce = lazyRetry(() => import('./components/landing/LifestyleImageGeneratorEcommerce').then(m => ({ default: m.LifestyleImageGeneratorEcommerce })));
const AIPrintOnDemandPhotos = lazyRetry(() => import('./components/landing/AIPrintOnDemandPhotos').then(m => ({ default: m.AIPrintOnDemandPhotos })));
const DFYPricingPage = lazyRetry(() => import('./components/landing/DFYPricingPage').then(m => ({ default: m.DFYPricingPage })));
const ShopifyConnectPage = lazyRetry(() => import('./components/ShopifyConnectPage').then(m => ({ default: m.ShopifyConnectPage })));
const ShopifyWelcomePage = lazyRetry(() => import('./components/ShopifyWelcomePage'));
const AuthCallbackPage = lazyRetry(() => import('./components/AuthCallbackPage'));
const UseCasesPage = lazyRetry(() => import('./components/UseCasesPage').then(m => ({ default: m.UseCasesPage })));
const DemosPage = lazyRetry(() => import('./components/DemosPage').then(m => ({ default: m.DemosPage })));
const ProductListingAgent = lazyRetry(() => import('./components/ProductListingAgent').then(m => ({ default: m.ProductListingAgent })));

// Session check component
function SessionCheck() {
  const session = useSession();
  const supabase = useSupabaseClient();
  const navigate = useNavigate();

  useEffect(() => {
    // Check if we have a session but it's invalid or expired
    if (session?.expires_at && Date.now() > session.expires_at * 1000) {
      console.log('Session expired, signing out...');
      supabase.auth.signOut().then(() => {
        navigate('/');
      });
    }
  }, [session, supabase, navigate]);

  // Listen for auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth state changed:', event, session?.user?.email);

      if (event === 'SIGNED_OUT') {
        // Don't leave product photos behind on a shared computer.
        clearAllToolDrafts();
        // A failed Google sign-in also fires SIGNED_OUT; stay on the callback page
        // so it can show the error.
        if (window.location.pathname !== '/auth/callback') {
          navigate('/');
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [supabase.auth, navigate]);

  return null;
}

// Component to handle browser back/forward cache (bfcache) issues
function BFCacheHandler() {
  const [isRestored, setIsRestored] = useState(false);

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        console.log('Page restored from bfcache, reloading...');
        setIsRestored(true);
        window.location.reload();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const fromStripe = sessionStorage.getItem('stripe_redirect');
        if (fromStripe) {
          console.log('Returned from Stripe, clearing flag');
          sessionStorage.removeItem('stripe_redirect');
        }
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  if (isRestored) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Restoring page...</p>
        </div>
      </div>
    );
  }

  return null;
}

function AppContent() {
  const location = useLocation();
  const navigationType = useNavigationType();

  // Client-side navigation keeps the previous page's scroll position, so links opened
  // pages part-way down. Start new pages at the top; back/forward keeps the browser's
  // own restore and hash links keep their anchor. Search-param changes don't scroll.
  useEffect(() => {
    if (navigationType !== 'POP' && !location.hash) window.scrollTo(0, 0);
  }, [location.pathname, navigationType]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: shellData, loading: shellLoading } = useShellData();

  useEffect(() => {
    const isBlogRoute = location.pathname.startsWith('/blog/');
    if (!shellLoading && !isBlogRoute) {
      document.dispatchEvent(new Event('render-event'));
      let marker = document.getElementById('prerender-marker');
      if (!marker) {
        marker = document.createElement('div');
        marker.id = 'prerender-marker';
        marker.setAttribute('data-prerender-ready', 'true');
        marker.style.display = 'none';
        document.body.appendChild(marker);
      }
    }
  }, [shellLoading, location.pathname]);

  const announcementSettings = shellData?.announcementBar || null;
  const [isDismissed, setIsDismissed] = useState(() => {
    return sessionStorage.getItem('announcement_bar_dismissed') === 'true';
  });

  const handleDismiss = () => {
    setIsDismissed(true);
    sessionStorage.setItem('announcement_bar_dismissed', 'true');
  };

  const isAnnouncementVisible = announcementSettings?.is_enabled && !isDismissed && !shellLoading;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <CountryParamCapture />
      <AnalyticsPageTracker />
      <BFCacheHandler />
      <SessionCheck />
      <Header menuItems={shellData?.headerMenu} logo={shellData?.logo} isLoading={shellLoading} />
      {isAnnouncementVisible && announcementSettings && (
        <AnnouncementBar settings={announcementSettings} onClose={handleDismiss} />
      )}
      <div className="flex-1" style={{ paddingTop: isAnnouncementVisible ? '140px' : '80px' }}>
        {/* Full height so the footer doesn't jump up while a page's code loads */}
        <Suspense fallback={<div className="min-h-screen"><LoadingIndicator /></div>}>
          {/* Client-only routes (not prerendered) must also be listed in APP_ROUTES in
              netlify/edge-functions/inject-canonical.ts, or crawlers get a 404 status. */}
          <Routes>
            <Route path="/" element={
              <>
                <SEOHead pageId="home" />
                <LandingPage siteStyles={shellData?.siteStyles || null} />
              </>
            } />
              <Route path="/remove-background" element={
                <>
                  <SEOHead pageId="background_remover" />
                  <BulkBackgroundRemover />
                </>
              } />
            <Route path="/blog-writer" element={
              <>
                <SEOHead pageId="blog_writer" />
                <BlogWriter />
              </>
            } />
            <Route path="/product-description" element={
              <>
                <SEOHead pageId="product_description" />
                <ProductDescriptionGenerator />
              </>
            } />
            <Route path="/product-collage" element={
              <>
                <SEOHead pageId="product_collage" />
                <ProductCollageMaker />
              </>
            } />
            <Route path="/social-media" element={
              <>
                <SEOHead pageId="social_media" />
                <SocialMediaContentGenerator />
              </>
            } />
            <Route path="/email-copy" element={
              <>
                <SEOHead pageId="email_copy" />
                <EmailCopyGenerator />
              </>
            } />
            <Route path="/banner-creator" element={
              <>
                <SEOHead pageId="banner_creator" />
                <BannerCreator />
              </>
            } />
            <Route path="/photo-retoucher" element={
              <>
                <SEOHead pageId="photo_retoucher" />
                <PhotoRetoucher />
              </>
            } />
            <Route path="/banner-generator" element={
              <>
                <SEOHead pageId="ai_banner_generator" />
                <BannerGenerator />
              </>
            } />
            <Route path="/gift-guide" element={
              <>
                <SEOHead pageId="gift_guide_creator" />
                <GiftGuideCreator />
              </>
            } />
            <Route path="/image-resizer" element={
              <>
                <SEOHead pageId="image_resizer" />
                <ImageResizer />
              </>
            } />
            <Route path="/lifestyle-image-generator" element={
              <>
                <SEOHead pageId="image_generator" />
                <ImageGenerator />
              </>
            } />
            <Route path="/seo-audit" element={
              <>
                <SEOHead pageId="seo_content_audit" />
                <SEOContentAudit />
              </>
            } />
            <Route path="/store-health-check" element={
              <>
                <SEOHead pageId="store_health_check" />
                <StoreHealthCheck />
              </>
            } />
            <Route path="/ad-copy" element={
              <>
                <SEOHead pageId="ad_copy_generator" noindex={true} />
                <AdCopyGenerator />
              </>
            } />
            <Route path="/reddit-scraper" element={
              <>
                <SEOHead pageId="reddit_scraper" />
                <RedditScraper />
              </>
            } />
            <Route path="/photo-studio" element={
              <>
                <SEOHead pageId="photo_studio" />
                <PhotoStudio />
              </>
            } />
            <Route path="/product-listing-agent" element={
              <>
                <SEOHead pageId="product_listing_agent" />
                <ProductListingAgent />
              </>
            } />
            <Route path="/pricing" element={
              <>
                <SEOHead pageId="pricing" />
                <PricingPage />
              </>
            } />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/blog" element={<BlogList />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/blog/:slug/txt" element={<BlogPostPlainText />} />
            <Route path="/login" element={
              <>
                <SEOHead pageId="home" noindex={true} />
                <SignInPage />
              </>
            } />
            <Route path="/signup" element={
              <>
                <SEOHead pageId="home" noindex={true} />
                <SignUpPage />
              </>
            } />
            <Route path="/set-password" element={
              <>
                <SEOHead pageId="home" noindex={true} />
                <SetPasswordPage />
              </>
            } />
            <Route path="/reset-password" element={
              <>
                <SEOHead pageId="home" noindex={true} />
                <ResetPasswordPage />
              </>
            } />
            <Route path="/auth/callback" element={
              <>
                <SEOHead pageId="home" noindex={true} />
                <AuthCallbackPage />
              </>
            } />
            <Route path="/verify-email" element={
              <>
                <SEOHead pageId="home" noindex={true} />
                <VerifyEmailPage />
              </>
            } />
            <Route path="/admin/login" element={
              <>
                <SEOHead pageId="home" noindex={true} />
                <AdminLogin />
              </>
            } />
            <Route path="/admin" element={
              <ProtectedRoute>
                <>
                  <SEOHead pageId="home" noindex={true} />
                  <AdminDashboard />
                </>
              </ProtectedRoute>
            } />
            <Route path="/admin/blog" element={
              <ProtectedRoute>
                <>
                  <SEOHead pageId="home" noindex={true} />
                  <BlogPostsList />
                </>
              </ProtectedRoute>
            } />
            <Route path="/admin/blog/new" element={
              <ProtectedRoute>
                <>
                  <SEOHead pageId="home" noindex={true} />
                  <BlogPostEditor />
                </>
              </ProtectedRoute>
            } />
            <Route path="/admin/blog/edit/:id" element={
              <ProtectedRoute>
                <>
                  <SEOHead pageId="home" noindex={true} />
                  <BlogPostEditor />
                </>
              </ProtectedRoute>
            } />
            <Route path="/admin/qa" element={
              <ProtectedRoute>
                <>
                  <SEOHead pageId="home" noindex={true} />
                  <QAApp />
                </>
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <>
                  <SEOHead pageId="home" noindex={true} />
                  <UserDashboard />
                </>
              </ProtectedRoute>
            } />
            <Route path="/shopify-welcome" element={
              <>
                <SEOHead pageId="home" noindex={true} />
                <ShopifyWelcomePage />
              </>
            } />
            <Route path="/connect-shopify" element={
              <ProtectedRoute>
                <>
                  <SEOHead pageId="home" noindex={true} />
                  <ShopifyConnectPage />
                </>
              </ProtectedRoute>
            } />
            <Route path="/bulk-product-description-writer" element={
              <>
                <SEOHead pageId="bulk_product_description_writer" />
                <BulkProductDescriptionWriter />
              </>
            } />
            <Route path="/automate-product-photos-for-dropshipping" element={
              <>
                <SEOHead pageId="automate_product_photos_dropshipping" />
                <AutomateDropshippingPhotos />
              </>
            } />
            <Route path="/ai-product-photos-for-jewelry" element={
              <>
                <SEOHead pageId="ai_product_photos_jewelry" />
                <AIJewelryPhotos />
              </>
            } />
            <Route path="/lifestyle-image-generator-for-ecommerce" element={
              <>
                <SEOHead pageId="lifestyle_image_generator_ecommerce" />
                <LifestyleImageGeneratorEcommerce />
              </>
            } />
            <Route path="/ai-product-photos-for-print-on-demand" element={
              <>
                <SEOHead pageId="ai_product_photos_pod" />
                <AIPrintOnDemandPhotos />
              </>
            } />
            <Route path="/done-for-you-content" element={
              <>
                <SEOHead pageId="dfy_pricing" />
                <DFYPricingPage />
              </>
            } />
            <Route path="/demos" element={
              <>
                <SEOHead
                  pageId="demos"
                  defaultTitle="Product Demos: See MerchantFloor in Action"
                  defaultDescription="Short walkthrough videos showing how to create product listings, studio-quality photos and lifestyle images with MerchantFloor."
                />
                <DemosPage />
              </>
            } />
            <Route path="/use-cases" element={
              <>
                <SEOHead pageId="home" />
                <UseCasesPage />
              </>
            } />
              <Route path="/:slug" element={<StaticPage />} />
              <Route path="*" element={
                <>
                  <SEOHead pageId="home" noindex={true} />
                  <NotFoundPage />
                </>
              } />
            </Routes>
          </Suspense>
        </div>
        <Footer links={shellData?.footerLinks} />
        <CookieConsentBanner />
      </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;